import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MailWarning, Loader2, X, Send } from "lucide-react";
import { useAuth } from "../lib/auth";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function EmailVerifyBanner() {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  // Only nag accounts that explicitly have an unverified email
  if (!user || user.email_verified !== false || dismissed) return null;

  async function handleResend() {
    setSending(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/auth/resend-verification`, {});
      setSent(true);
      toast.success(data.message || "Verification email sent. Check your inbox.");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Couldn't send verification email. Try again.");
    } finally {
      setSending(false);
    }
  }

  return (
    <div
      className="flex items-center gap-3 mb-6 px-4 py-3 rounded-xl border border-amber-300/60 bg-amber-50 text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-200"
      data-testid="email-verify-banner"
    >
      <MailWarning size={18} className="shrink-0 text-amber-600 dark:text-amber-400" />
      <div className="flex-1 min-w-0 text-sm">
        <span className="font-semibold">Verify your email</span>
        <span className="hidden sm:inline"> — confirm <strong>{user.email}</strong> to secure your account and unlock email features.</span>
      </div>
      {!sent ? (
        <button
          onClick={handleResend}
          disabled={sending}
          data-testid="resend-verification-btn"
          className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-600 text-white text-xs font-semibold hover:bg-amber-700 active:scale-[0.98] transition-all disabled:opacity-60"
        >
          {sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
          {sending ? "Sending…" : "Resend email"}
        </button>
      ) : (
        <span className="shrink-0 text-xs font-medium text-amber-700 dark:text-amber-300" data-testid="verify-sent-note">Sent ✓</span>
      )}
      <button
        onClick={() => setDismissed(true)}
        data-testid="dismiss-verify-banner"
        className="shrink-0 w-7 h-7 rounded-lg flex items-center justify-center hover:bg-amber-200/50 dark:hover:bg-amber-900/40 transition-colors"
        aria-label="Dismiss"
      >
        <X size={15} />
      </button>
    </div>
  );
}
